
import { supabase } from "@/integrations/supabase/client";

interface FilterState {
  ageMin: number;
  ageMax: number;
  maxDistance: number;
  onlineOnly: boolean;
  withPhotoOnly: boolean;
  lookingFor: string[];
}

export const favoritesService = {
  async addFavorite(currentUserId: string, favoriteUserId: string) {
    const { error } = await supabase
      .from('user_favorites')
      .insert({
        user_id: currentUserId,
        favorite_user_id: favoriteUserId
      });

    if (error) throw error;
  },

  async removeFavorite(currentUserId: string, favoriteUserId: string) {
    const { error } = await supabase
      .from('user_favorites')
      .delete()
      .eq('user_id', currentUserId)
      .eq('favorite_user_id', favoriteUserId);

    if (error) throw error;
  },

  async isFavorite(currentUserId: string, favoriteUserId: string) {
    const { data, error } = await supabase
      .from('user_favorites')
      .select('id')
      .eq('user_id', currentUserId)
      .eq('favorite_user_id', favoriteUserId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return !!data;
  },

  async getFavorites(currentUserId: string) {
    const { data, error } = await supabase
      .from('user_favorites')
      .select(`
        id,
        favorite_user_id,
        created_at,
        user_profiles!user_favorites_favorite_user_id_fkey (
          user_id,
          first_name,
          last_name,
          display_name,
          age,
          bio,
          location,
          photo_urls,
          public_interests
        )
      `)
      .eq('user_id', currentUserId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getFavoritesFiltered(currentUserId: string, filters: FilterState) {
    let query = supabase
      .from('user_favorites')
      .select(`
        id,
        favorite_user_id,
        created_at,
        user_profiles!user_favorites_favorite_user_id_fkey (
          user_id,
          first_name,
          last_name,
          display_name,
          age,
          bio,
          location,
          photo_urls,
          public_interests,
          looking_for
        )
      `)
      .eq('user_id', currentUserId)
      .order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    let filteredData = data || [];

    // Apply client-side filters
    if (filters.ageMin > 18 || filters.ageMax < 99) {
      filteredData = filteredData.filter((fav: any) => {
        const profile = fav.user_profiles;
        if (!profile?.age) return false;
        return profile.age >= filters.ageMin && profile.age <= filters.ageMax;
      });
    }

    if (filters.withPhotoOnly) {
      filteredData = filteredData.filter((fav: any) => {
        const profile = fav.user_profiles;
        return profile?.photo_urls && profile.photo_urls.length > 0;
      });
    }

    if (filters.lookingFor.length > 0) {
      filteredData = filteredData.filter((fav: any) => {
        const profile = fav.user_profiles;
        if (!profile?.looking_for) return false;
        return filters.lookingFor.some((looking: string) => 
          profile.looking_for.includes(looking)
        );
      });
    }

    return filteredData;
  },

  async getFavoriteCount(currentUserId: string) {
    const { count, error } = await supabase
      .from('user_favorites')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', currentUserId);

    if (error) throw error;
    return count || 0;
  },

  async getUsersWhoFavoritedMe(currentUserId: string, limit: number = 6) {
    const { data, error } = await supabase
      .from('user_favorites')
      .select(`
        id,
        user_id,
        created_at
      `)
      .eq('favorite_user_id', currentUserId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    
    if (!data || data.length === 0) return [];

    // Get user profiles for the users who favorited me
    const userIds = data.map(fav => fav.user_id);
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select(`
        user_id,
        first_name,
        last_name,
        display_name,
        age,
        bio,
        location,
        photo_urls,
        public_interests
      `)
      .in('user_id', userIds);

    if (profilesError) throw profilesError;

    // Combine favorites data with profiles, maintaining the order
    return data.map((fav: any) => {
      const profile = profiles?.find(p => p.user_id === fav.user_id);
      if (!profile) return null;
      
      return {
        ...profile,
        distance: 1, // Default distance since we don't have location context
        isOnline: false, // We don't have online status in favorites
        last_seen: null,
        favorited_at: fav.created_at
      };
    }).filter(Boolean);
  }
};
